import React, { useCallback, useEffect, useReducer, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Edit2, Trash2, Star, ExternalLink, LogOut, Search } from "lucide-react";
import { adminAPI } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { CATEGORY_MAP } from "../../lib/categories";
import Seo from "../../components/Seo";
import "./Admin.css";

const initialList = { articles: [], loading: true };
function listReducer(state, action) {
  switch (action.type) {
    case "loading": return { ...state, loading: true };
    case "ok": return { articles: action.articles, loading: false };
    case "err": return { ...state, loading: false };
    case "remove": return { ...state, articles: state.articles.filter((x) => x.id !== action.id) };
    case "feature": return { ...state, articles: state.articles.map((x) => ({ ...x, is_featured: x.id === action.id })) };
    default: return state;
  }
}

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [{ articles, loading }, dispatch] = useReducer(listReducer, initialList);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = useCallback(async (searchValue) => {
    dispatch({ type: "loading" });
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      const q = (searchValue !== undefined ? searchValue : search).trim();
      if (q) params.search = q;
      const r = await adminAPI.list(params);
      dispatch({ type: "ok", articles: r.data });
    } catch (e) {
      dispatch({ type: "err" });
    }
  }, [statusFilter, search]);

  useEffect(() => {
    load();
  }, [statusFilter, load]);

  const onSearch = (e) => { e.preventDefault(); load(search); };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    await adminAPI.remove(a.id);
    dispatch({ type: "remove", id: a.id });
  };

  const feature = async (a) => {
    await adminAPI.feature(a.id);
    dispatch({ type: "feature", id: a.id });
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="admin-shell" data-testid="admin-dashboard">
      <Seo title="Admin Dashboard" noIndex />
      <header className="admin-header">
        <div className="admin-header__inner">
          <Link to="/admin" className="admin-brand">RACE377 · Admin</Link>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span className="caption" style={{ color: "var(--text-secondary)" }}>{user?.email}</span>
            <button onClick={handleLogout} className="admin-link-btn" data-testid="admin-logout">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="admin-main">
        <div className="admin-toolbar">
          <h1 className="heading-2">Articles</h1>
          <Link to="/admin/articles/new" className="btn-primary" data-testid="admin-new-article">
            <Plus size={18} /> New article
          </Link>
        </div>

        <div className="admin-filters">
          <div className="admin-status-tabs">
            {["all", "published", "draft"].map((s) => (
              <button
                key={s}
                className={`admin-status-tab ${statusFilter === s ? "is-active" : ""}`}
                onClick={() => setStatusFilter(s)}
                data-testid={`admin-status-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
          <form className="admin-search" onSubmit={onSearch}>
            <Search size={16} />
            <input
              placeholder="Search articles…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-search-input"
            />
          </form>
        </div>

        {loading ? (
          <div className="loading-container"><div className="loading-spinner" /></div>
        ) : articles.length === 0 ? (
          <p className="body-medium" style={{ padding: "60px 0", color: "var(--text-secondary)" }}>
            No articles yet. <Link to="/admin/articles/new" style={{ color: "var(--brand-primary)" }}>Create your first one →</Link>
          </p>
        ) : (
          <table className="admin-table" data-testid="admin-articles-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Title</th>
                <th>Category</th>
                <th>Status</th>
                <th>Published</th>
                <th style={{ width: 180 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map((a) => (
                <tr key={a.id} data-testid={`admin-row-${a.id}`}>
                  <td>
                    <button
                      className={`admin-feature-btn ${a.is_featured ? "is-featured" : ""}`}
                      onClick={() => feature(a)}
                      title={a.is_featured ? "Featured" : "Set as featured"}
                      data-testid={`admin-feature-${a.id}`}
                    >
                      <Star size={16} fill={a.is_featured ? "currentColor" : "none"} />
                    </button>
                  </td>
                  <td>
                    <div className="admin-title-cell">
                      <strong>{a.title}</strong>
                      <span className="caption">/{a.slug}</span>
                    </div>
                  </td>
                  <td><span className="caption">{CATEGORY_MAP[a.category] || a.category}</span></td>
                  <td>
                    <span className={`admin-pill admin-pill--${a.status}`}>{a.status}</span>
                  </td>
                  <td>
                    {a.published_at
                      ? new Date(a.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                      : "—"}
                  </td>
                  <td>
                    <div className="admin-row-actions">
                      {a.status === "published" && (
                        <Link to={`/news/${a.slug}`} target="_blank" className="admin-icon-btn" title="View">
                          <ExternalLink size={15} />
                        </Link>
                      )}
                      <Link to={`/admin/articles/${a.id}`} className="admin-icon-btn" title="Edit" data-testid={`admin-edit-${a.id}`}>
                        <Edit2 size={15} />
                      </Link>
                      <button
                        className="admin-icon-btn admin-icon-btn--danger"
                        onClick={() => remove(a)}
                        title="Delete"
                        data-testid={`admin-delete-${a.id}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </main>
    </div>
  );
}
